import { defineStore } from 'pinia'
import apiClient from '@/services/apiClient'
import { unwrapApiData } from '@/services/apiEnvelope'

const defaultPersonalData = () => ({
    username: 'NONE',
    first_name: 'NONE',
    last_name: 'NONE',
    email: 'NONE',
})

const defaultSearchSettings = () => ({
    include_parts_base: true,
})

let settingsInFlight = null

export const useProfileStore = defineStore('profile', {
    state: () => ({
        personalData: defaultPersonalData(),
        searchSettings: defaultSearchSettings(),
        loading: false,
        settingsLoaded: false,
        error: null,
    }),
    getters: {
        includePartsBase: (state) => Boolean(state.searchSettings.include_parts_base),
    },
    actions: {
        reset() {
            this.personalData = defaultPersonalData()
            this.searchSettings = defaultSearchSettings()
            this.loading = false
            this.settingsLoaded = false
            this.error = null
        },
        setProfile(profile = {}) {
            const personalData = profile.personalData || profile.personal_data || {}
            const searchSettings = profile.searchSettings || profile.search_settings || {}

            this.personalData = {
                ...defaultPersonalData(),
                ...personalData,
            }

            this.searchSettings = {
                ...defaultSearchSettings(),
                ...searchSettings,
            }
        },
        setPersonalData(payload = {}) {
            this.personalData = {
                ...this.personalData,
                ...payload,
            }
        },
        setSearchSettings(payload = {}) {
            this.searchSettings = {
                ...this.searchSettings,
                ...payload,
            }
        },
        async fetchProfileSettings() {
            if (this.settingsLoaded) {
                return this.searchSettings
            }
            if (settingsInFlight) {
                return settingsInFlight
            }

            this.loading = true
            this.error = null

            settingsInFlight = (async () => {
                try {
                    const response = await apiClient.get('/api/profile/settings')

                    const data = unwrapApiData(response, {})
                    this.setSearchSettings({
                        include_parts_base:
                            typeof data?.include_parts_base === 'boolean'
                                ? data.include_parts_base
                                : this.searchSettings.include_parts_base,
                    })
                    this.settingsLoaded = true
                    return data
                } catch (error) {
                    this.error = error instanceof Error ? error.message : 'Unknown error'
                    console.error('Failed to fetch profile settings', error)
                    throw error
                } finally {
                    this.loading = false
                    settingsInFlight = null
                }
            })()

            return settingsInFlight
        },
        async saveProfileSettings(payload = {}) {
            this.loading = true
            this.error = null

            const includePartsBase =
                payload.include_parts_base !== undefined
                    ? payload.include_parts_base
                    : this.searchSettings.include_parts_base

            try {
                const response = await apiClient.post('/api/profile/settings', {
                    include_parts_base: includePartsBase,
                })

                unwrapApiData(response, {})

                this.setSearchSettings({ include_parts_base: includePartsBase })
                this.settingsLoaded = true
                return true
            } catch (error) {
                this.error = error instanceof Error ? error.message : 'Unknown error'
                console.error('Failed to save profile settings', error)
                throw error
            } finally {
                this.loading = false
            }
        },
    },
})
